import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/languageContext';

const LanguageChangeModal = ({ 
  isOpen, 
  onClose, 
  currentLanguage, 
  onLanguageChange, 
  onSuccess, 
  onError 
}) => {
  const { t } = useTranslation();
  const { isRTL, getDirectionClass } = useLanguage();
  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage || "en");
  const [isLoading, setIsLoading] = useState(false);

  const languages = [
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "ar", name: "العربية", flag: "🇸🇦" }
  ];

  const handleLanguageChange = async (e) => {
    e.preventDefault();
    
    if (selectedLanguage === currentLanguage) {
      onClose();
      return;
    }

    setIsLoading(true);

    try {
      const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";
      const response = await fetch(`${API_URL}/user/set-language`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ language: selectedLanguage })
      });

      const data = await response.json();

      if (response.ok) {
        onLanguageChange(selectedLanguage);
        onSuccess(t('profile.languageUpdated'));
        onClose();
      } else {
        onError(data.message || t('profile.failedToUpdate'));
      }
    } catch (error) {
      console.error('Error updating language:', error);
      onError(t('profile.errorOccurred'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-white p-6 rounded-lg shadow-xl w-96 max-w-md mx-4 ${getDirectionClass()}`}>
        <h3 className={`text-lg font-semibold mb-4 text-gray-900 ${isRTL ? 'text-right' : 'text-left'}`}>
          {t('profile.changeLanguage')}
        </h3>
        
        <form onSubmit={handleLanguageChange} className="space-y-4">
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('profile.selectLanguage')}
            </label>
            <div className="space-y-3">
              {languages.map((language) => (
                <label 
                  key={language.code}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedLanguage === language.code 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="language"
                    value={language.code}
                    checked={selectedLanguage === language.code}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                    className="sr-only"
                  />
                  <span className="text-2xl mr-3">{language.flag}</span>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{language.name}</div>
                    <div className="text-sm text-gray-500">
                      {language.code === "en" ? "English" : "Arabic"}
                    </div>
                  </div>
                  {selectedLanguage === language.code && (
                    <div className="text-blue-500">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </label>
              ))}
            </div>
          </div>
          
          <div className={`flex pt-4 ${isRTL ? 'justify-start space-x-reverse space-x-3' : 'justify-end space-x-3'}`}>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading || selectedLanguage === currentLanguage}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <div className={`animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                  {t('profile.updating')}
                </>
              ) : (
                t('profile.updateLanguage')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LanguageChangeModal; 